<template>
  <div class="auth-description">
    <h2 class="auth-title">
      {{ $t('Welcome back') }}
    </h2>
    <div>
      <span class="text text-grey">{{ $t("Don't have an account?") }}</span>
      <router-link :to="{ name: 'register', query: $router.currentRoute.query }" class="text link">
        {{ $t('Sign up') }}
      </router-link>
    </div>
    <Form class="login-form auth" @submit="submit">
      <FormInput
        id="email-input"
        v-model="email"
        type="email"
        placeholder="mail@example.com"
        :label-text="$t('Email')"
      />

      <FormInput
        id="pass-input"
        v-model="password"
        type="password"
        :placeholder="$t('Enter your password')"
        :label-text="$t('Password')"
        :error="!!error"
        :error-message="error"
      >
        {{ $t('Password') }}
        <router-link :to="{ name: 'forgot' }" :tabindex="2" class="link forgot-link">
          {{ $t('Forgot?') }}
        </router-link>
      </FormInput>

      <button type="submit" class="btn-primary w-button w-full">
        <Spinner v-if="isLoading" size="20" />
        <span v-else>
          {{ $t('Log in') }}
        </span>
      </button>
    </Form>
  </div>
</template>

<script>
  import { mapActions, mapMutations } from 'vuex';
  import { validationMixin } from 'vuelidate';
  import required from 'vuelidate/src/validators/required';
  import email from 'vuelidate/src/validators/email';
  import Form from '@/components/common/Form';
  import FormInput from '@/components/ui/FormInput';
  import Spinner from 'vue-simple-spinner';
  import parseError from '@/utils/formatting/parse-error';
  import request from '@/utils/api/request';

  const DEMO_USER = {
    email: 'demo@fixtender.com',
    password: '12345678',
  };

  export default {
    name: 'Login',
    components: { FormInput, Form, Spinner },
    mixins: [validationMixin],
    data() {
      return {
        email: '',
        password: '',
        error: null,
        isLoading: false,
      };
    },
    validations: {
      email: { required, email },
      password: { required },
    },
    methods: {
      ...mapActions({
        changeField: 'auth/changeField',
        setToken: 'auth/setToken',
        login: 'auth/login',
      }),
      ...mapMutations('auth', ['setRegistrationInfo', 'setPassword']),
      async submit() {
        this.isLoading = true;
        this.error = null;
        this.$v.$touch();
        if (!this.$v.$invalid) {
          try {
            const queryPublicTenderLinkHash = this.$router?.currentRoute?.query?.tender;

            await this.login({
              email: this.email,
              password: this.password,
              public_tender_link_hash: queryPublicTenderLinkHash || null,
            });

            location.reload();
          } catch (e) {
            if (e.response.status === 403) {
              this.setRegistrationInfo({ email: this.email });
              this.setPassword(this.password);
              if (e.response.data.type === 'EmailNotVerifiedError') {
                await request.post('/resend_confirm_email/', { email: this.email });
                await this.$router.push({
                  name: 'register',
                  query: {
                    step: 2,
                  },
                });
              } else if (e.response.data.type === 'PasswordIsEmptyError') {
                await this.$router.push({
                  name: 'register',
                  query: {
                    step: 1,
                  },
                });
              } else if (e.response.data.register_finish_token) {
                this.setToken(e.response.data.register_finish_token);

                e.response.data.is_team_created
                  ? await this.$router.push({
                      name: 'complete',
                      query: {
                        step: 2,
                      },
                    })
                  : await this.$router.push({
                      name: 'register',
                      query: {
                        step: 3,
                      },
                    });
              }
            }

            this.error = parseError(e, { returnDetail: true }) || e.message;
          }
        }
        this.isLoading = false;
      },
      demo() {
        this.email = DEMO_USER.email;
        this.password = DEMO_USER.password;
      },
    },
  };
</script>
